import cloneDeep from 'lodash/cloneDeep';
import isEmpty from 'lodash/isEmpty';
import isEqual from 'lodash/isEqual';
import { useEffect, useState } from 'react';
import { useFilterConfigs } from '../../../../../../hooks/useFilterConfigs';
import { useFilters } from '../../../../../../hooks/useFilters';
import type { Filter } from '../../../../../../stores/filters';
import { engagementDefaultCommonFilters } from '../../FilterConfig/EngagementDefaultCommonFilters';
import { defaultEngagementFilters, type FilterPrefix } from '../../FilterConfig/EngagementDefaultFilters';

interface UseEventFilterManagerProps {
  filterPrefix: FilterPrefix;
  onChange?: (filters: { [key: string]: Filter }) => void;
}

const useEventFilterManager = ({ filterPrefix, onChange }: UseEventFilterManagerProps) => {
  const { filters: appliedFilters } = useFilters();
  const [filters, setFilters] = useState<{ [key: string]: Filter }>({});
  const [isLoading, setIsLoading] = useState(false);
  const { filterConfigs } = useFilterConfigs();

  const defaultFilter: Filter = {
    filterName: `${filterPrefix}_events_filter`,
    expression: 'Yes',
    enabled: true,
    expressionType: 'target',
  };

  useEffect(() => {
    if (isEmpty(filters)) return;

    const defaultFilters = defaultEngagementFilters[filterPrefix];
    const defaultKeys = Object.keys(defaultFilters);

    const defaultFiltersChanged = defaultKeys.some((key) => !isEqual(filters[key], defaultFilters[key]));

    const changedMap = Object.entries(engagementDefaultCommonFilters).reduce(
      (acc, [key, { defaultExpression }]) => {
        const applied = appliedFilters[key];
        const current = filters[key];

        if (applied?.enabled && applied.expression !== '') {
          acc[key] = !!(current && !isEqual(current, applied));
        } else {
          acc[key] = !!(current && current.expression !== defaultExpression);
        }
        return acc;
      },
      {} as Record<string, boolean>,
    );

    const dependencyFilterKeys = filterConfigs
      .filter((c) => c.filterName === `${filterPrefix}_events_filter`)
      .flatMap((c) => c.secondaryFilters || [])
      .filter((key) => !((key as string) in defaultFilters));

    // Check if non-default filters have been added
    const hasExtraFilters = dependencyFilterKeys.some(
      (key) => !!filters[key as string] && filters[key as string].enabled && filters[key as string].expression !== '',
    );

    const anyChanged = defaultFiltersChanged || Object.values(changedMap).some(Boolean) || hasExtraFilters;

    if (anyChanged) {
      return;
    }

    // Reset filters if nothing has changed
    const filtersCopy = cloneDeep(filters);

    if (!defaultFiltersChanged) {
      defaultKeys.forEach((key) => {
        if (filtersCopy[key]) {
          filtersCopy[key].enabled = appliedFilters[key]?.enabled ?? false;
          filtersCopy[key].expression = appliedFilters[key]?.expression ?? '';
        }
      });
    }

    Object.entries(engagementDefaultCommonFilters).forEach(([key]) => {
      if (!changedMap[key] && filtersCopy[key]) {
        filtersCopy[key].enabled = appliedFilters[key]?.enabled ?? false;
        filtersCopy[key].expression = appliedFilters[key]?.expression ?? '';
      }
    });

    onChange?.(filtersCopy);
  }, [filters, appliedFilters, filterPrefix, onChange]);

  const handleChildChange = (filter: Filter) => {
    setFilters((filters) => {
      const newFilters = {
        ...filters,
        [filter.filterName]: filter,
        [defaultFilter.filterName]: defaultFilter,
      };

      if (onChange) {
        onChange(newFilters);
      }

      return newFilters;
    });
  };

  return {
    filters,
    isLoading,
    setIsLoading,
    handleChildChange,
  };
};

export default useEventFilterManager;
